import { z } from 'zod';

const BATCH_ID = '_id of the batch';

export const ExternalTag = {
  Authentication: 'Authentication',
  RetrieveData: 'Retrieve Data',
  HandleBatches: 'Handle Batches',
} as const;

// Read by the API's Swagger generator (ExternalSwaggerService); ts-rest ignores route metadata at runtime.
export interface ExternalRouteMetadata {
  tag: (typeof ExternalTag)[keyof typeof ExternalTag];
  // route is callable without the bearer token
  public?: boolean;
  // multipart routes: the uploaded `file` travels outside the zod body, so it is documented separately
  fileField?: string;
  // documents a 200 response the contract still types as z.any()
  responseDoc?: z.ZodTypeAny;
  // the 200 response is a file download served with one of these content types
  fileResponse?: string[];
}

export const ExternalErrorSchema = z.object({
  message: z.string(),
});

export const ExternalBatchNotFoundSchema = ExternalErrorSchema.describe('Batch not found');

export const ExternalMessageResponseSchema = z.object({
  message: z.string(),
});

export const ExternalBatchIdBodySchema = z.object({
  batch: z.string().describe(BATCH_ID),
});

export const ExternalAuthErrorResponses = {
  401: z.object({ reason: z.string() }).describe('Missing, invalid or expired bearer token'),
  403: ExternalErrorSchema.describe('The user is not an external API user'),
};

export const ExternalGetUserResponseSchema = z.object({
  _id: z.string().describe('unique identifier for the user on our system'),
  email: z.string(),
  phoneNumber: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

export type ExternalGetUserResponse = z.infer<typeof ExternalGetUserResponseSchema>;

export const ExternalGetTemplatesResponseSchema = z.object({
  _id: z.string().describe('unique identifier for the template on our system'),
  name: z.string().describe('name of the template'),
  type: z.string().describe('template type, e.g. low_value_h7 or high_value_h1'),
});

export type ExternalGetTemplatesResponse = z.infer<typeof ExternalGetTemplatesResponseSchema>;

export const ExternalGetCustomersResponseSchema = z.object({
  _id: z.string().describe('unique identifier for the customer on our system'),
  name: z.string().describe('name of the customer'),
});

export type ExternalGetCustomersResponse = z.infer<typeof ExternalGetCustomersResponseSchema>;

export const ExternalGetBrokersResponseSchema = z.object({
  _id: z.string().describe('unique identifier for the broker on our system'),
  name: z.string().describe('name of the broker'),
});

export type ExternalGetBrokersResponse = z.infer<typeof ExternalGetBrokersResponseSchema>;

// passthrough, not strict: unknown filters must reach the service, which rejects them with the
// partner-facing `<key> field is invalid!` message rather than a ts-rest schema error body.
export const ExternalGetBatchesBodySchema = z
  .object({
    batch: z.string().optional().describe(BATCH_ID),
    name: z.string().optional().describe('name of the batch'),
    startDate: z.string().optional().describe('creation start date in DD/MM/YYYY format. Must be used together with endDate'),
    endDate: z.string().optional().describe('creation end date in DD/MM/YYYY format. Must be used together with startDate'),
    isReleased: z.string().optional().describe('"true" or "false": retrieve only the released / not released batches'),
  })
  .passthrough();

export type ExternalGetBatchesBody = z.infer<typeof ExternalGetBatchesBodySchema>;

const statistic = (description: string) => z.number().nullable().optional().describe(`total of declaration lines ${description}`);

const ExternalBatchStatisticsSchema = z.object({
  accepted: statistic('in progress (customs status)'),
  inControl: statistic('in control (customs status)'),
  invalidated: statistic('invalidated (customs status)'),
  notReleased: statistic('not released (customs status)'),
  rejected: statistic('rejected (customs status)'),
  released: statistic('released (customs status)'),
  preAccepted: statistic('pre accepted (customs status)'),
  preInControl: statistic('pre in control (customs status)'),
  preSent: statistic('pre sent (customs status)'),
  sent: statistic('sent (customs status)'),
  total: statistic('in the batch'),
});

export const ExternalGetBatchesResponseSchema = z.object({
  _id: z.any().describe('unique identifier for the batch on our system'),
  createdAt: z.union([z.string(), z.date()]).nullable().optional().describe('when the batch was created'),
  customer: z.any().describe('_id of the customer'),
  declarant: z.any().describe('_id of the declarant'),
  deleted: z.boolean().nullable().optional().describe('whether the batch is deleted'),
  name: z.string().nullable().optional().describe('name of the batch'),
  status: z
    .object({
      details: z.string().nullable().optional().describe('error description when the batch failed'),
      value: z.string().nullable().optional().describe('batch status (success, in_progress, failed)'),
    })
    .optional(),
  filename: z.string().nullable().optional().describe('name of the file the batch was created from'),
  statistics: ExternalBatchStatisticsSchema.optional().describe('declaration line counts per customs status'),
});

export type ExternalGetBatchesResponse = z.infer<typeof ExternalGetBatchesResponseSchema>;

// forwarder deployments also expose the risk analysis counters on each batch
export const ExternalForwarderGetBatchesResponseSchema = ExternalGetBatchesResponseSchema.extend({
  statistics: ExternalBatchStatisticsSchema.extend({
    analysisWrongValue: statistic('flagged by the risk analysis for a wrong value'),
    analysisWrongDescription: statistic('flagged by the risk analysis for a wrong description'),
    analysisWrongHsCode: statistic('flagged by the risk analysis for a wrong HS code'),
  })
    .optional()
    .describe('declaration line counts per customs status, plus the risk analysis counters'),
});

export type ExternalForwarderGetBatchesResponse = z.infer<typeof ExternalForwarderGetBatchesResponseSchema>;

export const ExternalGetDeclarationsBodySchema = z.object({
  batch: z.string().optional().describe(BATCH_ID),
  LRN: z.string().optional().describe('LRN number of the declaration'),
  referenceNumberUCR: z.string().optional().describe('commercial reference number of the declaration'),
});

export type ExternalGetDeclarationsBody = z.infer<typeof ExternalGetDeclarationsBodySchema>;

export const ExternalGetAuditTrailBodySchema = z
  .object({
    batch: z.string().optional().describe('_id of the batch (provide either batch or LRN)'),
    LRN: z.string().optional().describe('LRN number of a declaration (provide either batch or LRN)'),
  })
  .refine((body) => !!body.batch !== !!body.LRN, { message: 'Provide exactly one of batch or LRN' });

export type ExternalGetAuditTrailBody = z.infer<typeof ExternalGetAuditTrailBodySchema>;

export const ExternalGetTransitQuerySchema = z.object({
  name: z.string().optional().describe('transit name filter'),
  type: z.enum(['All', 'Departure', 'Arrival']).optional().describe('transit type filter; All (default) returns both departure and arrival transits'),
  MRN: z.string().optional().describe('movement reference number'),
  startDate: z.string().optional().describe('creation start date in DD/MM/YYYY format. Must be used together with endDate'),
  endDate: z.string().optional().describe('creation end date in DD/MM/YYYY format. Must be used together with startDate'),
});

export type ExternalGetTransitQuery = z.infer<typeof ExternalGetTransitQuerySchema>;
